import type { EmploymentVerificationAction } from 'types/graphql'

import {
  VoEActionStatus,
  VoEOutreachType,
  VoEStatus,
} from 'src/graphql/types/verificationOfEmployments'
import { formatInternationalPhoneNumber } from 'src/lib/phone.utils'

/** Matches the dates shown in the audit trail (US month/day/year). */
export const OUTREACH_DATE_TIME_FORMAT = 'MM/DD/YYYY h:mm A'

/**
 * A fixed-width date column keeps the actor markers in one line regardless
 * of how wide the time reads ("8:04 PM" vs "10:05 PM").
 */
export const AUDIT_TRAIL_TIMESTAMP_CLASS =
  'w-[132px] shrink-0 whitespace-nowrap text-right tabular-nums text-doubleNickel-gray-500'

/** Actor markers (pills and avatars) share one height so they sit level. */
export const ACTOR_MARKER_CLASS = 'h-[18px]'

export const AUTOMATED_OUTREACH_LABEL = 'Sent by automated outreach'

const OUTREACH_TYPE_LABEL: Record<VoEOutreachType, string> = {
  [VoEOutreachType.EMAIL]: 'Email',
  [VoEOutreachType.CALL]: 'Call',
  [VoEOutreachType.FAX]: 'Fax',
}

export const OUTREACH_STATUS_COLOR: Record<VoEActionStatus, string> = {
  [VoEActionStatus.SCHEDULED]: 'bg-doubleNickel-blue-500',
  [VoEActionStatus.IN_PROGRESS]: 'bg-doubleNickel-warning-500',
  [VoEActionStatus.COMPLETED]: 'bg-doubleNickel-success-500',
  [VoEActionStatus.FAILED]: 'bg-doubleNickel-error-500',
  [VoEActionStatus.CANCELLED]: 'bg-doubleNickel-gray-400',
  [VoEActionStatus.SKIPPED]: 'bg-doubleNickel-gray-400',
}

type ActionLike = Pick<
  EmploymentVerificationAction,
  'type' | 'status' | 'scheduledAt' | 'configStepOrder' | 'callAttempt'
>

export const typeLabel = (type: string): string =>
  OUTREACH_TYPE_LABEL[type as VoEOutreachType] ?? type

export const isAutomatedAction = (
  action: Pick<ActionLike, 'configStepOrder' | 'callAttempt'>
): boolean => action.configStepOrder != null || action.callAttempt != null

export const attemptNumber = (
  action: Pick<ActionLike, 'configStepOrder' | 'callAttempt'>
): number | null => action.callAttempt ?? action.configStepOrder ?? null

export const getActionLabel = (action: ActionLike): string => {
  const attempt = attemptNumber(action)
  return attempt != null
    ? `${typeLabel(action.type)} · Attempt ${attempt}`
    : `Manual ${typeLabel(action.type)}`
}

const LIVE_ACTION_STATUSES: string[] = [
  VoEActionStatus.SCHEDULED,
  VoEActionStatus.IN_PROGRESS,
]

/** Mirrors the API's outreach eligibility: only open VoEs still get attempts. */
export const OUTREACH_ELIGIBLE_VOE_STATUSES: string[] = [
  VoEStatus.NOT_STARTED,
  VoEStatus.IN_PROGRESS,
]

/**
 * The one action a recruiter should see: the earliest still-pending attempt,
 * and only while the VoE can still receive outreach. Later attempts depend on
 * it, and a VoE that was answered or closed will have its rows swept.
 */
export const nextLiveAction = <T extends ActionLike>(
  actions: T[],
  voeStatus: string | null | undefined
): T | null => {
  if (!voeStatus || !OUTREACH_ELIGIBLE_VOE_STATUSES.includes(voeStatus)) {
    return null
  }
  return (
    [...actions]
      .filter((a) => LIVE_ACTION_STATUSES.includes(a.status))
      .sort(
        (a, b) =>
          new Date(a.scheduledAt).getTime() - new Date(b.scheduledAt).getTime()
      )[0] ?? null
  )
}

/** How close an added attempt can land to an automated one before we warn. */
export const NEARBY_AUTOMATED_WINDOW_MS = 60 * 60 * 1000

export const findNearbyAutomatedAction = <T extends ActionLike>(
  actions: T[],
  type: string,
  at: Date
): T | null => {
  const distance = (a: T) =>
    Math.abs(new Date(a.scheduledAt).getTime() - at.getTime())
  return (
    actions
      .filter(
        (a) =>
          a.type === type &&
          isAutomatedAction(a) &&
          LIVE_ACTION_STATUSES.includes(a.status) &&
          distance(a) <= NEARBY_AUTOMATED_WINDOW_MS
      )
      .sort((a, b) => distance(a) - distance(b))[0] ?? null
  )
}

export const contactForAction = (
  type: string,
  voe: {
    email?: string | null
    phoneNumber?: string | null
    faxNumber?: string | null
  }
): string | null => {
  switch (type) {
    case VoEOutreachType.EMAIL:
      return voe.email || null
    case VoEOutreachType.CALL:
      return voe.phoneNumber
        ? formatInternationalPhoneNumber(voe.phoneNumber)
        : null
    case VoEOutreachType.FAX:
      return voe.faxNumber
        ? formatInternationalPhoneNumber(voe.faxNumber)
        : null
    default:
      return null
  }
}
